import React, { useEffect, useState } from "react";
import "./Vendor.css";

// ─── Static Data ───────────────────────────────────────────────────────────────

const TICKER_ITEMS = [
  "🚀 EARLY ACCESS — LIMITED SELLER SPOTS OPEN",
  "💰 ZERO COMMISSION FOR FIRST 6 MONTHS",
  "♻️ USED SPARE PARTS SELLERS WELCOME",
  "📈 SELLERS PROJECTED TO EARN ₹2L-10L/MONTH",
  "🔥 INDIA'S FIRST USED + NEW AUTO PARTS MARKETPLACE",
  "⚡ SAME-DAY LISTINGS — START SELLING IN 24 HOURS",
];

const SCROLL_STATS = [
  { num: "₹1.2L Cr", label: "Market Size" },
  { num: "4Cr+", label: "Vehicles Needing Parts/Year" },
  { num: "80%", label: "Sellers Offline" },
  { num: "₹10L+", label: "Projected Monthly Earnings" },
  { num: "0%", label: "Commission (Early Access)" },
  { num: "24hrs", label: "Go‑Live Time" },
];

// Duplicate items for seamless infinite scroll loops
const TICKER_DOUBLED = [...TICKER_ITEMS, ...TICKER_ITEMS];
const SCROLL_STATS_DOUBLED = [...SCROLL_STATS, ...SCROLL_STATS];

const formatIndian = (num) => num.toLocaleString("en-IN");

// ─── Component ─────────────────────────────────────────────────────────────────

const BecomeSeller = () => {
  // ── Calculator state ──
  const [products, setProducts] = useState(200);
  const [price, setPrice] = useState(800);
  const [conv, setConv] = useState(3);
  const [visitors, setVisitors] = useState(15);

  // ── FOMO counter ──
  const [spotsLeft, setSpotsLeft] = useState(127);

  // ── Toast ──
  const [toastMsg, setToastMsg] = useState("");
  const [toastVisible, setToastVisible] = useState(false);

  // ── Form state ──
  const [form, setForm] = useState({
    biz: "",
    name: "",
    phone: "",
    city: "",
    type: "",
    qty: "",
    gst: "",
    source: "",
    terms: false,
  });

  // ── Derived calculator values ──
  const orders = Math.round(products * visitors * (conv / 100));
  const revenue = orders * price;
  const daily = Math.round(revenue / 30);
  const yearly = revenue * 12;
  const yearlyDisplay =
    yearly >= 10_000_000
      ? (yearly / 10_000_000).toFixed(1) + "Cr"
      : (yearly / 100_000).toFixed(1) + "L";

  // ── Spots counter simulation ──
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setSpotsLeft((prev) => (prev > 50 ? prev - 1 : prev));
      }
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // ── Intersection Observer for card entrance animations ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );

    const targets = document.querySelectorAll(".vision-card, .mt-step, .uc-card");
    targets.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      el.style.transition = "all 0.6s ease-out";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // ── Helpers ──
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 5000);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const submitForm = (e) => {
    e.preventDefault();
    showToast(
      `🎉 Welcome aboard, ${form.name}! "${form.biz}" has been registered. We'll call you within 24 hours.`
    );
    setForm({
      biz: "",
      name: "",
      phone: "",
      city: "",
      type: "",
      qty: "",
      gst: "",
      source: "",
      terms: false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Render ──
  return (
    <>
      {/* ── SCROLLING TICKER ── */}
      <div className="ticker-wrap">
        <div className="ticker">
          {TICKER_DOUBLED.map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </div>
      </div>

      {/* ── HEADER ── */}
      <header className="header">
        <div className="header-inner">
          <button className="header-cta" onClick={() => scrollTo("reg-section")}>
            Apply Now — Free
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="container">
          <div className="urgency-pill">
            <span className="dot" />
            Phase 1 — Early Access Open Now
          </div>

          <h1>
            The Auto Parts Industry
            <br />
            is <span className="hot">₹1.2 Lakh Crore.</span>
            <br />
            Your Share is <span className="grad">Waiting.</span>
          </h1>

          <p className="hero-sub">
            India has <strong>4 crore+ vehicles</strong> needing parts every year. Yet 80% of sellers are
            invisible online. SparePe changes that — we're building the <strong>Amazon of auto parts</strong>,
            and we need sellers like you on Day 1.
          </p>

          <p className="hero-hook">⚡ Zero commission for 6 months. Zero listing fees. Forever.</p>

          <div className="hero-cta-wrap">
            <button className="btn-mega primary" onClick={() => scrollTo("reg-section")}>
              🚀 Claim Your Seller Spot — Free
            </button>
            <button className="btn-mega ghost" onClick={() => scrollTo("calc-section")}>
              💰 Calculate My Earnings
            </button>
          </div>

          <div className="proof-strip">
            <div className="proof-item">
              🔒 <span className="val">0%</span> commission for early sellers
            </div>
            <div className="proof-item">
              ⚡ Go live in <span className="val">24 hours</span>
            </div>
            <div className="proof-item">
              ♻️ New <span className="val">&amp;</span> Used parts welcome
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        {/* ── SCROLLING STATS BANNER ── */}
        <div className="scroll-banner" style={{ margin: "0 -20px 64px", paddingLeft: "20px", paddingRight: "20px" }}>
          <div className="scroll-track">
            {SCROLL_STATS_DOUBLED.map((item, i) => (
              <div className="scroll-item" key={i}>
                <span className="num">{item.num}</span> {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* ── USED SPARE PARTS CALLOUT ── */}
        <div className="used-callout">
          <div className="uc-badge">♻️ FIRST IN INDIA</div>
          <div className="uc-title">
            Scrapyard Owners. Used Parts Dealers.
            <br />
            <span
              style={{
                background: "var(--grad2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              This Platform Was Built For You.
            </span>
          </div>
          <div className="uc-desc">
            Nobody else in India lets used spare part sellers sell online with dignity. No more roadside bargaining.
            No more wasted inventory. SparePe gives your{" "}
            <strong style={{ color: "var(--text)" }}>used parts the same premium listing</strong> as OEM parts —
            because sustainability is the future, and you're sitting on gold.
          </div>
          <div className="uc-grid">
            <div className="uc-card">
              <div className="uc-card-icon">🏗️</div>
              <div className="uc-card-title">Scrapyard Owners</div>
              <div className="uc-card-desc">
                List thousands of salvaged parts. Reach mechanics who need affordable options for their customers.
              </div>
            </div>
            <div className="uc-card">
              <div className="uc-card-icon">♻️</div>
              <div className="uc-card-title">Used Parts Dealers</div>
              <div className="uc-card-desc">
                Your stock finally gets online visibility. Sell across cities, not just your neighborhood.
              </div>
            </div>
            <div className="uc-card">
              <div className="uc-card-icon">🔧</div>
              <div className="uc-card-title">Garages with Old Stock</div>
              <div className="uc-card-desc">
                Got spare parts piling up from old jobs? List and liquidate them to other garages instantly.
              </div>
            </div>
          </div>
        </div>

        {/* ── REVENUE CALCULATOR ── */}
        <div className="calc-section" id="calc-section">
          <div className="calc-header">
            <h2>💰 How Much Can You Earn?</h2>
            <p>Adjust the sliders to see your projected monthly revenue on SparePe</p>
          </div>
          <div className="calc-body">
            <div className="calc-inputs">
              <div className="calc-group">
                <div className="calc-label">
                  <span>Products Listed</span>
                  <span className="val">{products.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  className="calc-slider"
                  min="10"
                  max="5000"
                  value={products}
                  onChange={(e) => setProducts(parseInt(e.target.value))}
                />
              </div>

              <div className="calc-group">
                <div className="calc-label">
                  <span>Avg. Price per Part (₹)</span>
                  <span className="val">₹{price.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  className="calc-slider"
                  min="50"
                  max="15000"
                  step="50"
                  value={price}
                  onChange={(e) => setPrice(parseInt(e.target.value))}
                />
              </div>

              <div className="calc-group">
                <div className="calc-label">
                  <span>Conversion Rate</span>
                  <span className="val">{conv}%</span>
                </div>
                <input
                  type="range"
                  className="calc-slider"
                  min="1"
                  max="15"
                  value={conv}
                  onChange={(e) => setConv(parseInt(e.target.value))}
                />
              </div>

              <div className="calc-group">
                <div className="calc-label">
                  <span>Monthly Visitors (per listing)</span>
                  <span className="val">{visitors}</span>
                </div>
                <input
                  type="range"
                  className="calc-slider"
                  min="2"
                  max="100"
                  value={visitors}
                  onChange={(e) => setVisitors(parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="calc-results">
              <div className="calc-results-inner">
                <div className="cr-label">Projected Monthly Revenue</div>
                <div className="cr-amount">₹{formatIndian(revenue)}</div>
                <div className="cr-period">per month (estimated)</div>
                <div className="cr-breakdown">
                  <div className="cr-item">
                    <div className="cr-item-val">{orders.toLocaleString()}</div>
                    <div className="cr-item-lbl">Est. Orders</div>
                  </div>
                  <div className="cr-item">
                    <div className="cr-item-val">₹{formatIndian(daily)}</div>
                    <div className="cr-item-lbl">Daily Revenue</div>
                  </div>
                  <div className="cr-item">
                    <div className="cr-item-val">₹{yearlyDisplay}</div>
                    <div className="cr-item-lbl">Annual Revenue</div>
                  </div>
                  <div className="cr-item">
                    <div className="cr-item-val" style={{ color: "var(--success)" }}>
                      ₹0
                    </div>
                    <div className="cr-item-lbl">Commission (6mo)</div>
                  </div>
                </div>
                <div className="cr-note">
                  * Based on marketplace averages. Early sellers get zero commission for 6 months.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── VISION / WHY NOW ── */}
        <div className="vision-section">
          <div className="vision-header">
            <h2>
              Why The Smartest Sellers
              <br />
              Are{" "}
              <span
                style={{
                  background: "var(--grad2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Joining Now
              </span>
            </h2>
            <p>
              The auto parts industry is going through its "smartphone moment." Sellers who go digital first will
              dominate. Here's why early movers win:
            </p>
          </div>
          <div className="vision-grid">
            <div className="vision-card">
              <div className="vc-icon">🥇</div>
              <div className="vc-title">First Mover Monopoly</div>
              <div className="vc-desc">
                SparePe ranks early sellers higher in search results <strong style={{ color: "var(--text)" }}>permanently.</strong>{" "}
                The first 500 sellers in each city will always appear on top. Once these spots fill, they're gone.
              </div>
              <div className="vc-highlight" style={{ background: "rgba(99,102,241,0.12)", color: "#818CF8" }}>
                👑 Top Ranking — Locked Forever
              </div>
            </div>
            <div className="vision-card">
              <div className="vc-icon">🆓</div>
              <div className="vc-title">Zero Commission. No Catch.</div>
              <div className="vc-desc">
                For the first 6 months, you pay <strong style={{ color: "var(--text)" }}>absolutely nothing.</strong> No listing fees.
                No commission. No hidden charges. We make money only when you're already making money.
              </div>
              <div className="vc-highlight" style={{ background: "rgba(16,185,129,0.12)", color: "#34D399" }}>
                💰 ₹0 Cost — 6 Months Free
              </div>
            </div>
            <div className="vision-card">
              <div className="vc-icon">📱</div>
              <div className="vc-title">We Bring Buyers To You</div>
              <div className="vc-desc">
                We're spending ₹5Cr on digital marketing this year. Every garage, mechanic, and car owner in India will know SparePe.{" "}
                <strong style={{ color: "var(--text)" }}>Your products get traffic, not just a listing.</strong>
              </div>
              <div className="vc-highlight" style={{ background: "rgba(6,182,212,0.12)", color: "#22D3EE" }}>
                🎯 ₹5Cr Marketing Spend — You Benefit
              </div>
            </div>
            <div className="vision-card">
              <div className="vc-icon">🛡️</div>
              <div className="vc-title">Built-In Trust Engine</div>
              <div className="vc-desc">
                Every seller gets a <strong style={{ color: "var(--text)" }}>verified badge, ratings, and reviews.</strong> Buyers trust verified sellers 4X more. Your reputation compounds — early advantage is permanent.
              </div>
              <div className="vc-highlight" style={{ background: "rgba(245,158,11,0.12)", color: "#FBBF24" }}>
                ✅ Verified Seller Badge — Day 1
              </div>
            </div>
          </div>
        </div>

        {/* ── MONEY TIMELINE ── */}
        <div className="money-section">
          <div className="money-header">
            <h2>Your First 90 Days on SparePe</h2>
            <p>Here's exactly what happens after you join — and how the money starts flowing</p>
          </div>
          <div className="money-timeline">
            <div className="mt-step">
              <div className="mt-dot" style={{ background: "rgba(99,102,241,0.15)", border: "2px solid var(--primary)" }}>
                📝
              </div>
              <div className="mt-card">
                <div className="mt-title">Day 1 — You Sign Up</div>
                <div className="mt-desc">
                  Fill a 5-minute form. Our team verifies your business within 24 hours. Your store goes live with a verified seller badge. Upload products via bulk upload or one-by-one.
                </div>
              </div>
            </div>

            <div className="mt-step">
              <div className="mt-dot" style={{ background: "rgba(6,182,212,0.15)", border: "2px solid var(--accent)" }}>
                📦
              </div>
              <div className="mt-card">
                <div className="mt-title">Week 1 — Products Go Live</div>
                <div className="mt-desc">
                  Your parts appear in search when garages and mechanics look for specific components. Used parts get a dedicated "Affordable" badge that buyers love.
                </div>
                <div className="mt-revenue" style={{ background: "rgba(6,182,212,0.12)", color: "#22D3EE" }}>
                  📊 Expected: 10-30 enquiries
                </div>
              </div>
            </div>

            <div className="mt-step">
              <div className="mt-dot" style={{ background: "rgba(16,185,129,0.15)", border: "2px solid var(--success)" }}>
                💰
              </div>
              <div className="mt-card">
                <div className="mt-title">Month 1 — First Revenue Hits</div>
                <div className="mt-desc">
                  Orders start flowing. You get paid directly to your bank account. Zero commission taken. Our logistics handles pickup if needed. You just pack and ship.
                </div>
                <div className="mt-revenue" style={{ background: "rgba(16,185,129,0.12)", color: "#34D399" }}>
                  💰 Expected: ₹50K — ₹2L revenue
                </div>
              </div>
            </div>

            <div className="mt-step">
              <div className="mt-dot" style={{ background: "rgba(245,158,11,0.15)", border: "2px solid var(--warning)" }}>
                🚀
              </div>
              <div className="mt-card">
                <div className="mt-title">Month 3 — You're Established</div>
                <div className="mt-desc">
                  Repeat buyers, growing reviews, rising in search rankings. Your early-mover advantage is now a permanent competitive moat. Other sellers joining later will never catch up.
                </div>
                <div className="mt-revenue" style={{ background: "rgba(245,158,11,0.12)", color: "#FBBF24" }}>
                  🔥 Expected: ₹2L — ₹10L revenue
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── FOMO SPOTS ── */}
        <div className="fomo-section">
          <div className="fomo-badge">
            <span style={{ display: "inline-block", width: "8px", height: "8px", background: "var(--danger)", borderRadius: "50%", animation: "blink 1s infinite" }} />{" "}
            Limited Availability
          </div>
          <div className="fomo-title">
            Only <span style={{ color: "var(--danger)" }}>{spotsLeft}</span> Early Seller Spots Left
          </div>
          <div className="fomo-desc">
            We're onboarding sellers city-by-city. Phase 1 cities are filling up fast. Once all spots for your city are taken, you'll be waitlisted.
          </div>
          <div className="spots-counter">
            <div className="spot-block">
              <div className="spot-val good">Delhi NCR</div>
              <div className="spot-lbl">23 spots left</div>
            </div>
            <div className="spot-block">
              <div className="spot-val good">Mumbai</div>
              <div className="spot-lbl">18 spots left</div>
            </div>
            <div className="spot-block">
              <div className="spot-val danger">Bangalore</div>
              <div className="spot-lbl">7 spots left</div>
            </div>
            <div className="spot-block">
              <div className="spot-val good">Hyderabad</div>
              <div className="spot-lbl">31 spots left</div>
            </div>
          </div>
          <div className="progress-bar-wrap">
            <div className="progress-label">
              <span>Spots Claimed</span>
              <span>373 / 500</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: "74.6%" }} />
            </div>
          </div>
          <button className="btn-mega primary" onClick={() => scrollTo("reg-section")}>
            🚀 Claim My Spot Now — It's Free
          </button>
        </div>

        {/* ── REGISTRATION FORM ── */}
        <div className="reg-section" id="reg-section">
          <div className="reg-header">
            <h2>🚀 Apply for Early Access</h2>
            <p>Takes less than 3 minutes. No fees. No commitment. Start selling within 24 hours.</p>
            <div className="micro-note">🔒 Your info is 100% secure &amp; never shared</div>
          </div>

          <form className="reg-form" onSubmit={submitForm}>
            <div className="form-row">
              <div>
                <label className="form-label">Business Name *</label>
                <input
                  type="text"
                  className="form-input"
                  name="biz"
                  value={form.biz}
                  placeholder="e.g. Sharma Auto Parts"
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  className="form-input"
                  name="name"
                  value={form.name}
                  placeholder="e.g. Rajinder Sharma"
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div>
                <label className="form-label">Phone (WhatsApp) *</label>
                <input
                  type="tel"
                  className="form-input"
                  name="phone"
                  value={form.phone}
                  placeholder="+91 98712 34567"
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">City *</label>
                <input
                  type="text"
                  className="form-input"
                  name="city"
                  value={form.city}
                  placeholder="e.g. New Delhi"
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">What do you sell? *</label>
              <select className="form-select form-input" name="type" value={form.type} onChange={handleFormChange} required>
                <option value="">Select your primary business</option>
                <option value="used_parts">♻️ Used / Salvaged Spare Parts</option>
                <option value="scrapyard">🏗️ Scrapyard / Dismantler</option>
                <option value="new_parts">🆕 New Aftermarket Parts</option>
                <option value="oem">🏭 OEM / Genuine Parts</option>
                <option value="mixed">📦 Both New &amp; Used Parts</option>
                <option value="accessories">🚗 Car Accessories</option>
                <option value="tyres">🛞 Tyres &amp; Batteries</option>
                <option value="oils">🛢️ Oils &amp; Lubricants</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Approx. Products You Can List</label>
              <select className="form-select form-input" name="qty" value={form.qty} onChange={handleFormChange}>
                <option value="">Select range</option>
                <option value="1-50">Under 50 parts</option>
                <option value="50-200">50 — 200 parts</option>
                <option value="200-1000">200 — 1,000 parts</option>
                <option value="1000+">1,000+ parts (we'll help with bulk upload)</option>
              </select>
            </div>

            <div className="form-row">
              <div>
                <label className="form-label">GST Number (if available)</label>
                <input
                  type="text"
                  className="form-input"
                  name="gst"
                  value={form.gst}
                  placeholder="Optional — can add later"
                  onChange={handleFormChange}
                />
              </div>
              <div>
                <label className="form-label">How did you hear about us?</label>
                <select className="form-select form-input" name="source" value={form.source} onChange={handleFormChange}>
                  <option value="">Select</option>
                  <option value="google">Google Search</option>
                  <option value="social">Social Media</option>
                  <option value="friend">Friend / Referral</option>
                  <option value="mechanic">Mechanic / Garage</option>
                  <option value="ad">Online Ad</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-check">
              <input type="checkbox" id="s-terms" name="terms" checked={form.terms} onChange={handleFormChange} required />
              <label htmlFor="s-terms">
                I want to join SparePe's Early Access program. I understand there are zero fees and I can start selling within 24 hours of approval.
              </label>
            </div>

            <button type="submit" className="form-submit">
              🚀 Submit &amp; Claim My Spot
            </button>

            <div className="form-trust">
              <span>🔒 256-bit Encrypted</span>
              <span>✅ No Credit Card Needed</span>
              <span>⚡ 24hr Approval</span>
              <span>🆓 Zero Fees</span>
            </div>
          </form>
        </div>

        {/* ── FINAL CTA ── */}
        <div className="final-cta">
          <h2>
            The Next Flipkart of
            <br />
            <span style={{ background: "var(--grad1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Auto Parts
            </span>{" "}
            is Launching.
          </h2>
          <p>
            Flipkart's early sellers became millionaires. Amazon's early sellers built empires. SparePe is your chance. Don't watch from the sidelines.
          </p>
          <button className="btn-mega primary" onClick={() => scrollTo("reg-section")}>
            🚀 I Want In — Apply Free
          </button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="container">
          <p>© 2026 SparePe Technologies Pvt. Ltd. — Reimagining India's Auto Parts Supply Chain</p>
        </div>
      </footer>

      {/* ── TOAST ── */}
      <div className={`toast${toastVisible ? " show" : ""}`}>{toastMsg}</div>
    </>
  );
};

export default BecomeSeller;
